import React, { useEffect } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Mail, Calendar, Users, Sparkles, ChevronRight } from 'lucide-react';

const LandingPage = () => {
  const { login, user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('auth_error');
    if (err === 'unauthorized') {
      toast.error('Access denied. Your Google account is not authorized for Rise. Please contact the administrator.');
      // Clean the URL so the toast does not re-fire on refresh
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const features = [
    {
      icon: Target,
      title: 'Multi-Source Discovery',
      description: 'Scan Google Maps, Reddit, news sites, and Instagram to find qualified leads automatically.'
    },
    {
      icon: Sparkles,
      title: 'AI-Powered Scoring',
      description: 'Claude analyzes each lead, scores fit and timing, and recommends the perfect outreach angle.'
    },
    {
      icon: Mail,
      title: 'Personalized Outreach',
      description: 'AI drafts emails that feel genuinely researched, using your segment profiles and lead context.'
    },
    {
      icon: Calendar,
      title: 'Meeting Scheduling',
      description: 'Schedule meetings directly from lead records with Google Calendar integration.'
    }
  ];

  const segments = [
    'Coffee Shops',
    'Fast-Casual Cafes', 
    'Food Trucks',
    'Restaurants',
    'Hotels',
    'Corporate Catering',
    'Event Caterers'
  ];

  return (
    <div className="min-h-screen bg-alabaster">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-alabaster/80 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-forest rounded-lg flex items-center justify-center">
                <span className="text-white font-bold font-heading">R</span>
              </div>
              <span className="font-heading font-bold text-xl text-forest">Rise</span>
            </div>
            <div>
              {user ? (
                <Button 
                  onClick={() => window.location.href = '/dashboard'}
                  className="bg-forest hover:bg-forest-dark text-white"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button 
                  onClick={() => window.location.href = '/login'}
                  className="bg-forest hover:bg-forest-dark text-white"
                  data-testid="login-btn"
                >
                  Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage text-forest text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered Lead Generation
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6 leading-tight">
                Find New Clients for Your{' '}
                <span className="text-forest">Wholesale Bakery</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                Rise automatically discovers potential clients, scores them with AI, and helps you send personalized outreach that converts.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={login}
                  className="bg-wheat hover:bg-wheat-dark text-white shadow-lg hover:shadow-xl rounded-full px-8 py-6 text-lg font-bold transition-all"
                  data-testid="get-started-btn"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-forest text-forest hover:bg-sage rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative animate-slide-in">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/7552714/pexels-photo-7552714.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Bakery owner managing business"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 animate-fade-in">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">New Leads This Week</p>
                    <p className="font-heading font-bold text-xl">+24</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Segments Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-muted-foreground mb-8">Target the right segments for your bakery</p>
          <div className="flex flex-wrap justify-center gap-4">
            {segments.map((segment) => (
              <span 
                key={segment}
                className="px-4 py-2 bg-sage rounded-full text-forest font-medium text-sm"
              >
                {segment}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-4">
              Everything You Need to Grow
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From discovery to closing, Rise handles your entire lead generation pipeline.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="p-8 bg-white rounded-2xl border border-stone-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-sage rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="h-6 w-6 text-forest" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-3">{feature.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-forest text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4">
              How Rise Works
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Three simple steps to fill your pipeline with qualified leads.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Set Your Markets', desc: 'Define target locations and business categories for discovery.' },
              { step: '02', title: 'Run Discovery', desc: 'AI scans multiple sources and scores each lead automatically.' },
              { step: '03', title: 'Send & Convert', desc: 'Review AI-drafted emails, approve, and track responses.' }
            ].map((item, index) => (
              <div key={item.step} className="relative">
                <div className="text-6xl font-heading font-bold text-white/10 mb-4">{item.step}</div>
                <h3 className="font-heading font-semibold text-xl mb-3">{item.title}</h3>
                <p className="text-white/70">{item.desc}</p>
                {index < 2 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 h-8 w-8 text-white/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-6">
            Ready to Grow Your Wholesale Business?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join bakeries using Rise to find and convert new clients every week.
          </p>
          <Button 
            onClick={login}
            className="bg-wheat hover:bg-wheat-dark text-white shadow-lg hover:shadow-xl rounded-full px-10 py-6 text-lg font-bold transition-all"
            data-testid="cta-get-started-btn"
          >
            Start Finding Leads
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-forest rounded-lg flex items-center justify-center">
                <span className="text-white font-bold font-heading">R</span>
              </div>
              <span className="font-heading font-bold text-xl">Rise</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </a>
              <a href="mailto:hello@maddonutshop.com" className="text-gray-400 hover:text-white transition-colors">
                Contact
              </a>
            </div>
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Rise. Wholesale bakery lead generation.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
